import * as SwitchPrimitives from "@radix-ui/react-switch";
import * as React from "react";
import { cn } from "../../../lib/cn";
import HStack from "../../Layout/HStack";

let switchLabelIdCounter = 0;

const Switch = React.forwardRef(
  /**
   * @param {Object} props
   * @param {string} [props.className] - Additional classes for the switch container
   * @param {string} [props.label] - Label text to display next to the switch
   * @param {string} [props.labelClassName] - Additional classes for the label
   * @param {boolean} [props.labelLeft=true] - Whether to show the label on the left side
   * @param {boolean} [props.checked] - Controlled checked state
   * @param {(checked: boolean) => void} [props.onCheckedChange] - Callback when checked state changes
   * @param {boolean} [props.disabled] - Whether the switch is disabled
   * @param {React.Ref<HTMLButtonElement>} ref - Forwarded ref
   * @returns {JSX.Element}
   */
  ({ className, label, labelClassName, labelLeft = true, ...props }, ref) => {
    const generatedLabelIdRef = React.useRef(null);
    if (!generatedLabelIdRef.current) {
      switchLabelIdCounter += 1;
      generatedLabelIdRef.current = `switch-label-${switchLabelIdCounter}`;
    }
    const labelId = generatedLabelIdRef.current;
    const localRef = React.useRef(null);
    const setRefs = React.useCallback(
      (node) => {
        localRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );
    const ariaLabelledBy = props["aria-labelledby"] || (!props["aria-label"] && label ? labelId : undefined);
    const handleLabelClick = () => {
      if (props.disabled) return;
      localRef.current?.click();
      localRef.current?.focus();
    };

    const switchElement = (
      <SwitchPrimitives.Root
        className={cn(
          "peer group flex !h-5 !w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors px-0.5 py-0.5",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-cta-high-contrast focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[state=checked]:bg-app-primary-cta-high-contrast data-[state=unchecked]:bg-app-menu-color-active",
          className
        )}
        aria-labelledby={ariaLabelledBy}
        {...props}
        ref={setRefs}
      >
        <SwitchPrimitives.Thumb
          style={{
            boxShadow: "0 0 10px 0px rgba(0, 0, 0, 0.3)",
          }}
          className={cn("block h-[22px] w-[22px] rounded-full bg-white transition-all translate-y-0 data-[state=checked]:translate-x-4 data-[state=unchecked]:-translate-x-0.5")}
        />
      </SwitchPrimitives.Root>
    );

    if (!label) return switchElement;

    const labelElement = (
      <span
        id={labelId}
        className={cn("text-lg cursor-pointer font-inter", labelClassName)}
        onClick={handleLabelClick}
      >
        {label}
      </span>
    );

    return (
      <HStack spacing={4}>
        {labelLeft && labelElement}
        {switchElement}
        {!labelLeft && labelElement}
      </HStack>
    );
  }
);

Switch.displayName = "Switch";

export { Switch };
